import { useState } from "react";
import { product, reviews } from "@/mocks/product";
import PageBanner from "@/components/feature/PageBanner";
import SizeGuideModal from "./components/SizeGuideModal";
import { useWhatsAppOrder } from "@/hooks/useWhatsAppOrder";

function StarIcon({ filled }: { filled: boolean }) {
  if (filled) {
    return (
      <i className="ri-star-fill w-3.5 h-3.5 flex items-center justify-center text-secondary-500 text-sm" />
    );
  }
  return (
    <i className="ri-star-line w-3.5 h-3.5 flex items-center justify-center text-secondary-500 text-sm" />
  );
}

function StarIconLg({ filled }: { filled: boolean }) {
  if (filled) {
    return (
      <i className="ri-star-fill w-5 h-5 flex items-center justify-center text-secondary-500" />
    );
  }
  return (
    <i className="ri-star-line w-5 h-5 flex items-center justify-center text-secondary-500" />
  );
}

export default function ProductDetail() {
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState("M");
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const { openModal } = useWhatsAppOrder();

  const images = product.images;
  const thumbnailLabels = ["Front", "Back", "Side", "Detail"];

  const specEntries = [
    { label: "Fabric", value: product.specs.fabric },
    { label: "Set", value: product.specs.set },
    { label: "Fit", value: product.specs.fit },
    { label: "Length", value: product.specs.length },
    { label: "Neck", value: product.specs.neck },
    { label: "Sleeve", value: product.specs.sleeve },
    { label: "Pattern", value: product.specs.pattern },
    { label: "Colour", value: product.specs.colour },
    { label: "Stitch", value: product.specs.stitch },
    { label: "Occasion", value: product.specs.occasion },
    { label: "Country of Origin", value: product.specs.country },
  ];

  return (
    <div>
      {/* ── Hero Banner ── */}
      <PageBanner
        title={product.name}
        subtitle="Premium cotton ethnic wear, designed for everyday elegance."
        bgImage="/images/product-detail-banner.jpg"
      />

      {/* ── Two-Column Gallery + Buy Box ── */}
      <section className="py-10 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
            {/* LEFT: Image Gallery */}
            <div className="flex flex-col sm:flex-row gap-4 lg:w-auto">
              {/* Main image — modest portrait size */}
              <div className="relative rounded-2xl overflow-hidden aspect-[3/4] w-full sm:w-[340px] lg:w-[400px] flex-shrink-0 bg-background-100">
                <img
                  src={images[selectedImage].src}
                  alt={images[selectedImage].alt}
                  className="w-full h-full object-cover object-top transition-opacity duration-300"
                />
              </div>

              {/* Thumbnail strip */}
              <div className="flex sm:flex-col gap-2.5 sm:w-24 lg:w-28 flex-shrink-0">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`relative flex-shrink-0 w-[72px] h-[96px] sm:w-24 sm:h-32 lg:w-28 lg:h-36 rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                      selectedImage === index
                        ? "border-accent-500 ring-2 ring-accent-300/40"
                        : "border-background-200 hover:border-accent-300"
                    }`}
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/65 via-black/25 to-transparent px-1.5 pt-3 pb-1">
                      <span className="text-white text-[10px] font-semibold leading-tight block text-center drop-shadow-sm">
                        {thumbnailLabels[index]}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* RIGHT: Buy Box */}
            <div className="flex flex-col flex-1 min-w-0">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-semibold tracking-wide uppercase mb-3 w-fit">
                <i className="ri-sparkling-line w-3 h-3 flex items-center justify-center" />
                New Arrival
              </span>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-accent-800 mb-1">
                {product.name}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base mb-4">
                {product.subtitle}
              </p>

              {/* Price */}
              <div className="flex items-center gap-3 mb-5">
                <span className="text-2xl md:text-3xl font-bold text-foreground-950">
                  ₹{product.price}
                </span>
                <span className="text-lg text-foreground-400 line-through">
                  ₹{product.originalPrice}
                </span>
                <span className="px-2.5 py-0.5 bg-secondary-100 text-secondary-900 text-xs font-semibold rounded-md">
                  {product.discount}
                </span>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-6">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <StarIcon key={i} filled={i <= Math.floor(product.rating)} />
                  ))}
                </div>
                <span className="text-sm text-foreground-600">
                  {product.rating} ★ · {product.ratingCount} ratings on {product.ratingSource}
                </span>
              </div>

              {/* Size Selector */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm font-semibold text-foreground-800">
                    Select Size
                  </span>
                  <button
                    onClick={() => setSizeGuideOpen(true)}
                    className="text-xs text-foreground-400 hover:text-secondary-600 underline underline-offset-2 transition-colors cursor-pointer"
                  >
                    Size Guide
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-6 py-2.5 rounded-lg text-sm font-medium border-2 transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        selectedSize === size
                          ? "border-accent-500 bg-accent-500 text-white"
                          : "border-background-200 text-foreground-600 hover:border-accent-300 bg-background-50"
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col gap-3 mb-4">
                <a
                  href="https://rzp.io/rzp/neoroop-flower-printed-set"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 text-white rounded-lg text-base font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap w-full"
                >
                  Buy Now — Pay Online
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </a>
                <button
                  type="button"
                  onClick={() => openModal(product.name, selectedSize)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-accent-500 text-accent-700 rounded-lg text-sm font-medium hover:bg-accent-50 transition-colors whitespace-nowrap w-full cursor-pointer"
                >
                  <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center" />
                  Order on WhatsApp
                </button>
              </div>

              {/* Trust line */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500 mb-2">
                <span className="inline-flex items-center gap-1">
                  <i className="ri-shield-check-line w-3.5 h-3.5 flex items-center justify-center text-secondary-500" />
                  Secure online payment
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-truck-line w-3.5 h-3.5 flex items-center justify-center text-secondary-500" />
                  Flat ₹30 shipping
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-arrow-go-back-line w-3.5 h-3.5 flex items-center justify-center text-secondary-500" />
                  7-day easy returns
                </span>
              </div>

              {/* COD note */}
              <p className="text-xs text-foreground-400 mt-1">
                💬 Prefer Cash on Delivery? Order via WhatsApp.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Product Highlights ── */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-accent-800 text-center mb-8">
            Product Highlights
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-background-200 rounded-xl overflow-hidden">
            {specEntries.map((spec) => (
              <div
                key={spec.label}
                className="bg-background-50 px-5 py-4"
              >
                <span className="block text-xs font-medium text-foreground-400 uppercase tracking-wide mb-1">
                  {spec.label}
                </span>
                <span className="block text-sm font-semibold text-foreground-800">
                  {spec.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Description ── */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-accent-800 text-center mb-8">
            About this set
          </h2>
          <div className="space-y-5">
            {product.description.map((para, i) => (
              <p
                key={i}
                className="text-foreground-600 text-sm md:text-base leading-relaxed text-center max-w-2xl mx-auto"
              >
                {para}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* ── Reviews ── */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-8 md:mb-10">
            <div className="inline-flex items-center gap-2 mb-3">
              <i className="ri-star-fill w-4 h-4 flex items-center justify-center text-secondary-500" />
              <span className="text-sm font-medium text-secondary-800 tracking-wide uppercase">
                Customer Reviews
              </span>
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-accent-800 mb-4">
              What our customers say
            </h2>
            <div className="inline-flex items-center gap-2 px-5 py-2 bg-secondary-100 rounded-full">
              <span className="text-lg font-bold text-secondary-900">
                {product.rating}
              </span>
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <StarIconLg key={i} filled={i <= Math.floor(product.rating)} />
                ))}
              </div>
              <span className="text-sm text-secondary-800">
                · Trusted on {product.ratingSource}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
            {reviews.slice(0, 6).map((review) => (
              <div
                key={review.id}
                className="bg-background-50 rounded-xl p-6 shadow-sm border border-background-200/70"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600 font-semibold text-sm flex-shrink-0">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-accent-800">
                      {review.name}
                    </p>
                    <p className="text-xs text-foreground-500">{review.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 mb-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <StarIcon key={i} filled={i <= review.rating} />
                  ))}
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  &ldquo;{review.text}&rdquo;
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-10 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="relative rounded-2xl overflow-hidden max-w-4xl mx-auto">
            <div className="w-full h-[240px] md:h-[300px] bg-gradient-to-br from-[#5B2A42] to-[#D98AA0]" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-2">
                Ready to make it yours?
              </h2>
              <p className="text-white/70 text-sm md:text-base max-w-md mb-6">
                Flat ₹30 shipping across India. 7-day easy returns.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <a
                  href="https://rzp.io/rzp/neoroop-flower-printed-set"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  Buy Now — Pay Online
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </a>
                <button
                  type="button"
                  onClick={() => openModal(product.name)}
                  className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-secondary-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line w-3.5 h-3.5 flex items-center justify-center" />
                  or order on WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Size Guide Modal ── */}
      <SizeGuideModal open={sizeGuideOpen} onClose={() => setSizeGuideOpen(false)} />
    </div>
  );
}